<script setup lang="ts">
/**
 * StatusDot — colored dot + label combo.
 *
 * Variants from prototype (181 occurrences):
 *   - active   green
 *   - trial    blue
 *   - pending  amber
 *   - suspended red
 *   - inactive grey
 *
 * Used in row status cells, account state indicators, etc.
 */
import { computed } from 'vue'

interface Props {
  status?: 'active' | 'trial' | 'pending' | 'suspended' | 'inactive'
}

const props = withDefaults(defineProps<Props>(), { status: 'active' })

const dotClass = computed(() => {
  return {
    active: 'bg-green',
    trial: 'bg-blue',
    pending: 'bg-amber',
    suspended: 'bg-red',
    inactive: 'bg-text-5',
  }[props.status]
})

const labelClass = computed(() => {
  return {
    active: 'text-green',
    trial: 'text-blue',
    pending: 'text-amber',
    suspended: 'text-red',
    inactive: 'text-text-4',
  }[props.status]
})
</script>

<template>
  <span class="inline-flex items-center gap-1.5 text-xs">
    <span
      :class="['w-1.5 h-1.5 rounded-full', dotClass]"
      aria-hidden="true"
    />
    <span :class="labelClass"><slot /></span>
  </span>
</template>
